import React, { useState, FunctionComponent } from 'react';
import isEmpty from 'lodash/isEmpty';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { BaseInputProps } from '@indigo-ag/component-module/dist/commonjs/components/_internal/BaseInput';
import { Input, Button, palettes } from '@indigo-ag/component-module';
import styled from 'styled-components';
import i18next from 'core/i18n';

import { KEY_PRESSED } from 'shared/constants';
import { text } from 'shared/utils';
import { faCheckCircle, faStream } from '@fortawesome/pro-solid-svg-icons';

const Main = styled.div`
  margin-bottom: 10px;
`;

const FlexLayout = styled.div`
  display: flex;
`;

const OptionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgb(255, 255, 255);
  border-radius: 6px;
  box-shadow: rgba(0, 87, 125, 0.14) 0px 2px 4px, rgba(0, 87, 125, 0.1) 0px 2px 2px;
  padding: 0 24px;
  margin: 5px 0;
`;
const OptionItemText = styled.div`
  margin-left: 10px;
  font-size: 15px;
`;

const StyledInput = styled(Input)`
  width: 100%;
`;

const StyledButton = styled(Button)`
  align-self: center;
  height: 50px;
  margin-top: 8px;
  margin-left: 13px;
`;

interface AddItemsListProps {
  label?: string;
  className?: string;
  inputProps?: BaseInputProps;
  optionsList?: string[];
  onOptionChange?: (changedOptions: string[]) => void;
  optionIconProps?: FontAwesomeIconProps;
  addButtonText?: string;
  removeButtonText?: string;
}

export const AddItemsList: FunctionComponent<AddItemsListProps> = ({
  label,
  inputProps,
  className,
  optionsList,
  onOptionChange,
  optionIconProps,
  addButtonText = 'add',
  removeButtonText = 'remove'
}) => {
  /**
   * state
   */
  const [itemsList, setItemsList] = useState(optionsList || []);
  const [inputValue, setInputValue] = useState<string>('');

  /**
   * callbacks
   */
  const handleAddOption = () => {
    if (!isEmpty(inputValue)) {
      const updatedOptions = [...itemsList, inputValue];
      setItemsList(updatedOptions);
      setInputValue('');
      if (onOptionChange) {
        onOptionChange(updatedOptions);
      }
    }
  };

  const handleRemoveOption = (removedOption: string) => {
    const updatedOptions = [...itemsList.filter(item => item !== removedOption)];
    setItemsList(updatedOptions);
    if (onOptionChange) {
      onOptionChange(updatedOptions);
    }
  };

  /**
   * default props
   */
  const baseInputProps: BaseInputProps = {
    id: 'add-items-list',
    label: i18next.t(label || ''),
    onChange: ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => setInputValue(value),
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.keyCode === KEY_PRESSED.ENTER) {
        handleAddOption();
      }
    },
    placeholder: i18next.t('pleaseType'),
    faIconBefore: faStream
  };
  const inputProperties: BaseInputProps = inputProps ? { ...baseInputProps, ...inputProps } : baseInputProps;
  const iconDefaultProps: FontAwesomeIconProps = optionIconProps
    ? optionIconProps
    : {
        color: palettes.brand.blue.base,
        size: '1x',
        icon: faCheckCircle
      };
  return (
    <Main className={className}>
      <FlexLayout>
        <StyledInput value={!isEmpty(inputValue) ? inputValue : ''} {...inputProperties} />
        <StyledButton onClick={handleAddOption} disabled={isEmpty(inputValue)} variant="secondary">
          {i18next.t(addButtonText)}
        </StyledButton>
      </FlexLayout>
      {optionsList &&
        optionsList.map((optionItem, index) => (
          <OptionItem key={index}>
            <FlexLayout>
              <FontAwesomeIcon {...iconDefaultProps} />
              <OptionItemText>{text.firstLetterUppercase(optionItem)}</OptionItemText>
            </FlexLayout>
            <Button variant="tertiary" onClick={() => handleRemoveOption(optionItem)}>
              {i18next.t(removeButtonText)}
            </Button>
          </OptionItem>
        ))}
    </Main>
  );
};
